import { localizedStrings } from '@core/strings'
import React, { useState } from 'react'
import { useModal } from 'webapp/components/common/GlobalModal'
import {
  DialogTitle,
  IconButton,
  DialogContent,
  Button,
  Tab,
  Tabs,
  CircularProgress,
} from '@common/material'
import ClearIcon from '@mui/icons-material/Clear'
import QRCode from 'react-qr-code'
import { Flex, Text, Box } from '@fundamentals'
import { ModalDialogContainer } from '@common/modal'
import { useClipboard } from 'webapp/providers/useClipboard'
import { UseMutationResult } from '@tanstack/react-query'
import { RequestErrorType } from '@core/utils/api-axios-instance'
import Utils from '@core/utils/utils'
import { useToast } from '@core/toast'

interface ShareQRCodeModalProps<TMutationParams> {
  title: string
  entityUrl: string
  entityLinkLabel: string
  entityLinkDescription: string
  pdfGenerationFailedMessage: string
  pdfMutation: UseMutationResult<
    { urlGET: string; urlDownload: string; expiryInMinutes: number },
    RequestErrorType,
    TMutationParams
  >
  pdfMutationParams: TMutationParams
}

type LinkType = 'entity' | 'pdf'

export const ShareQRCodeModal = <TMutationParams,>({
  title,
  entityUrl,
  entityLinkLabel,
  entityLinkDescription,
  pdfGenerationFailedMessage,
  pdfMutation,
  pdfMutationParams,
}: ShareQRCodeModalProps<TMutationParams>) => {
  const { hideModal } = useModal()
  const { copyToClipboard } = useClipboard()
  const toast = useToast()
  const [selectedType, setSelectedType] = useState<LinkType>('entity')
  const [pdfUrl, setPdfUrl] = useState<string | null>(null)
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false)

  const handleGeneratePdf = async () => {
    setIsGeneratingPdf(true)
    try {
      const pdfDownloadUrl = await pdfMutation.mutateAsync(pdfMutationParams)

      const fileGenerated = await Utils.pollForSuccessfulRequest(
        pdfDownloadUrl.urlDownload,
      )

      if (!fileGenerated) {
        toast.error(pdfGenerationFailedMessage)
        setSelectedType('entity')
      } else {
        setPdfUrl(pdfDownloadUrl.urlGET)
      }
    } catch (error) {
      toast.error(pdfGenerationFailedMessage)
      setSelectedType('entity')
    } finally {
      setIsGeneratingPdf(false)
    }
  }

  const handleTabChange = async (
    _event: React.SyntheticEvent,
    newValue: LinkType,
  ) => {
    setSelectedType(newValue)
    if (newValue === 'pdf' && !pdfUrl && !isGeneratingPdf) {
      await handleGeneratePdf()
    }
  }

  const handleCopyLink = () => {
    const urlToCopy = selectedType === 'entity' ? entityUrl : pdfUrl
    if (urlToCopy) {
      copyToClipboard(urlToCopy)
    }
  }

  const currentUrl = selectedType === 'entity' ? entityUrl : pdfUrl
  const currentDescription =
    selectedType === 'entity'
      ? entityLinkDescription
      : localizedStrings.copyLinkToPDFDescription

  return (
    <ModalDialogContainer>
      <DialogTitle
        variant='h5'
        display='flex'
        justifyContent='space-between'
        alignItems='center'
        sx={{ pr: 1, fontSize: 20 }}
      >
        {title}
        <IconButton aria-label='close-modal' onClick={hideModal}>
          <ClearIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        <Box mb={3}>
          <Tabs value={selectedType} onChange={handleTabChange}>
            <Tab
              label={entityLinkLabel}
              value='entity'
              sx={{
                textTransform: 'none',
                '&:focus': {
                  outline: 'none',
                },
              }}
            />
            <Tab
              label={localizedStrings.pdfLink}
              value='pdf'
              sx={{
                textTransform: 'none',
                '&:focus': {
                  outline: 'none',
                },
              }}
            />
          </Tabs>
        </Box>

        <Flex flexDirection='column' alignItems='center'>
          <Text
            variant='body2'
            color='text.secondary'
            mb={3}
            textAlign='center'
          >
            {currentDescription}
          </Text>
          <Box
            mb={3}
            minHeight={256}
            display='flex'
            alignItems='center'
            justifyContent='center'
          >
            {isGeneratingPdf ? (
              <CircularProgress />
            ) : currentUrl ? (
              <QRCode value={currentUrl} />
            ) : null}
          </Box>

          <Button
            variant='outlined'
            onClick={handleCopyLink}
            disabled={!currentUrl || isGeneratingPdf}
          >
            {localizedStrings.copyLink}
          </Button>
        </Flex>
      </DialogContent>
    </ModalDialogContainer>
  )
}
